import "react-international-phone/style.css"

import { Combobox, InputBase, MantineSize, ScrollArea, Text, TextInput, type TextInputProps, useCombobox, Box } from "@mantine/core"
import parsePhoneNumber, { type CountryCode } from "libphonenumber-js"
import { memo, useMemo, useState } from "react"
import { type CountryData, type CountryIso2, defaultCountries, FlagImage, parseCountry, usePhoneInput } from "react-international-phone"
import { IconPhone } from "@tabler/icons-react"
import classes from "./PhoneInput.module.css"

const DEFAULT_COUNTRIES: CountryIso2[] = ["us", "ca"]

/**
 * Validates a phone number and returns the parsed number.
 * @param value - The phone number to validate.
 * @returns The parsed phone number or an empty string if the number is invalid.
 */
export function validatePhoneNumber(value: string) {
  const parsedPhoneNumber = parsePhoneNumber(value, DEFAULT_COUNTRIES[0].toUpperCase() as CountryCode)

  if (!parsedPhoneNumber) return ""

  return parsedPhoneNumber.number
}

const CountryOption = memo(function CountryOption({ country }: { country: ReturnType<typeof parseCountry> }) {
  return (
    <div className={classes.countryOptionRow}>
      <FlagImage iso2={country.iso2} />
      <Text>{country.name}</Text>
      <Text c={"dimmed"}>(+{country.dialCode})</Text>
    </div>
  )
})

const CountrySelector = memo(function CountrySelector({
  disabled,
  country,
  setCountry,
  allowedCountries = defaultCountries,
}: {
  disabled?: boolean
  country: { iso2: CountryIso2 }
  setCountry: (iso2: CountryIso2) => void
  allowedCountries?: Array<CountryData>
}) {
  const [search, setSearch] = useState("")
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      combobox.focusTarget()
      setSearch("")
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput()
    },
  })

  const filteredCountries = useMemo(() => {
    const cleanedSearch = search.toLowerCase().trim()
    return allowedCountries.filter((c) => {
      const parsed = parseCountry(c)
      return parsed.name.toLowerCase().includes(cleanedSearch) || parsed.dialCode.toLowerCase().includes(cleanedSearch)
    })
  }, [allowedCountries, search])

  const countryOptions = useMemo(() => {
    return filteredCountries.map((c) => {
      const parsed = parseCountry(c)
      return (
        <Combobox.Option value={parsed.iso2} key={parsed.iso2}>
          <CountryOption country={parsed} />
        </Combobox.Option>
      )
    })
  }, [filteredCountries])

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        setCountry(val)
        combobox.closeDropdown()
      }}
      classNames={{
        dropdown: classes.dropdownWidth,
      }}
      disabled={disabled}
    >
      <Combobox.Target withAriaAttributes={false}>
        <InputBase
          variant={"unstyled"}
          component={"button"}
          type={"button"}
          pointer
          disabled={disabled}
          classNames={{
            input: classes.transparentBg,
          }}
          onClick={() => {
            if (disabled) return
            combobox.toggleDropdown()
          }}
          styles={{
            wrapper: {
              marginBottom: 0,
            },
          }}
        >
          <Box className={classes.countryContent}>
            <FlagImage iso2={country.iso2} className={classes.flagImage} />
            <Combobox.Chevron />
          </Box>
        </InputBase>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search value={search} onChange={(event) => setSearch(event.currentTarget.value)} placeholder={"Search country code"} />
        <Combobox.Options>
          <ScrollArea.Autosize type={"scroll"} mah={200}>
            {countryOptions.length > 0 ? countryOptions : <Combobox.Empty>No country found</Combobox.Empty>}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
})

interface IPhoneInput extends TextInputProps {
  value: string
  onChangePhone: (newPhoneValue: string) => void
  size?: MantineSize
  disabled?: boolean
  label?: string
  placeholder?: string
  classNames?: TextInputProps["classNames"]
  allowedCountries?: CountryIso2[] | false // If false, we dont show the country selector. If undefined or emtpy, we show all countries.
  preferredCountries?: CountryIso2[]
}

export const PhoneInput = memo(function PhoneInput({ value, onChangePhone, size = "md", disabled, label, placeholder, classNames, allowedCountries, preferredCountries, ...props }: IPhoneInput) {
  const leftSectionSize = useMemo(() => {
    switch (size) {
      case "sm":
        return 85
      case "md":
      default:
        return 95
    }
  }, [size])

  const allowedCountriesData: Array<CountryData> = useMemo(() => {
    return defaultCountries.filter((c) => {
      if (allowedCountries === undefined) return true
      if (allowedCountries === false) {
        return DEFAULT_COUNTRIES.includes(parseCountry(c).iso2)
      }
      return allowedCountries.includes(parseCountry(c).iso2)
    })
  }, [allowedCountries])

  const preferredCountriesData = useMemo(() => {
    // It must return an array of iso2 codes from the preferredCountries array that are also in the allowedCountriesData array
    const preferredCountriesData = (preferredCountries ?? DEFAULT_COUNTRIES).filter((c) => allowedCountriesData.some((c2) => parseCountry(c2).iso2 === c))

    return preferredCountriesData
  }, [allowedCountriesData, preferredCountries])

  const validatedPhoneValue = useMemo(() => {
    return validatePhoneNumber(value)
  }, [value])

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry: DEFAULT_COUNTRIES[0],
    value: validatedPhoneValue,
    countries: allowedCountriesData,
    preferredCountries: preferredCountriesData,
    forceDialCode: true,
    onChange: (data) => {
      onChangePhone(data.phone)
    },
  })

  const shouldShowCountrySelector = useMemo(() => {
    if (allowedCountries === false) return false
    return allowedCountriesData.length > 0
  }, [allowedCountries, allowedCountriesData])

  return (
    <TextInput
      miw={160}
      size={size}
      label={label}
      placeholder={placeholder}
      value={inputValue}
      onChange={handlePhoneValueChange}
      type={"tel"}
      ref={inputRef}
      leftSectionWidth={shouldShowCountrySelector ? leftSectionSize : undefined}
      disabled={disabled}
      classNames={{
        section: shouldShowCountrySelector ? classes.sectionLeft : undefined,
        input: shouldShowCountrySelector ? classes.inputPadding : undefined,
        ...classNames,
      }}
      leftSection={shouldShowCountrySelector ? <CountrySelector disabled={disabled} country={country} setCountry={setCountry} allowedCountries={allowedCountriesData} /> : <IconPhone size={16} />}
      {...props}
    />
  )
})
